import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  TextField, 
  Select, 
  MenuItem, 
  Button,
  FormControl,
  Menu,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TodayIcon from '@mui/icons-material/Today';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { 
  updateSavingsSettings,
  formatTimeFromSeconds, 
  formatTimeDisplayForNavbar,
  calculateCostSaving,
  calculateTotalTimeSaved,
  calculateTodayTimeSaved,
  setTranscriptionMetrics
} from '../store/savingsSlice';
import { useGetTranscriptionMetricsQuery, useGetTimeSavingsSettingsQuery, useUpdateTimeSavingsSettingsMutation } from '../store/userApi';

// Define different widget views
type WidgetView = 'main' | 'settings';

// Properties for currency options
interface CurrencyOption {
  value: string;
  label: string;
  symbol: string;
}

// List of available currencies
const currencies: CurrencyOption[] = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' },
  { value: 'MXN', label: 'MXN ($)', symbol: '$' }
];

interface TimeSavingsWidgetProps {
  isPro?: boolean;
}

const TimeSavingsWidget: React.FC<TimeSavingsWidgetProps> = ({ isPro = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State to control the current widget view
  const [view, setView] = useState<WidgetView>('main');
  
  // State for the menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  // Local state for the form
  const [localHourlyRate, setLocalHourlyRate] = useState<string>("50");
  
  // Access Redux state
  const dispatch = useDispatch();
  const { settings, transcriptionMetrics } = useSelector(
    (state: RootState) => state.savings
  );
  
  // Get transcription metrics from API
  const { data: metricsData, isLoading, refetch: refetchMetrics } = useGetTranscriptionMetricsQuery();
  
  // Get time savings settings from API
  const { data: settingsData, isLoading: isLoadingSettings } = useGetTimeSavingsSettingsQuery();
  
  // Update time savings settings mutation
  const [updateTimeSavingsSettings, { isLoading: isSavingSettings }] = useUpdateTimeSavingsSettingsMutation();
  
  // Calculate time saved values using new logic
  const totalTimeSavedSeconds = calculateTotalTimeSaved(transcriptionMetrics);
  const todayTimeSavedSeconds = calculateTodayTimeSaved(transcriptionMetrics);
  
  // Format times
  const totalTimeSaved = formatTimeFromSeconds(totalTimeSavedSeconds);
  const todayTimeSaved = formatTimeFromSeconds(todayTimeSavedSeconds);
  
  // Calculate costs saved based on the new time saved calculation
  const totalCostSaved = calculateCostSaving(totalTimeSavedSeconds, settings.hourlyRate);
  const todayCostSaved = calculateCostSaving(todayTimeSavedSeconds, settings.hourlyRate);

  // Get the symbol of the selected currency
  const getCurrencySymbol = () => {
    const found = currencies.find(c => c.value === settings.currency);
    return found ? found.symbol : '$';
  };
  
  // Update metrics when they arrive from the API
  useEffect(() => {
    if (metricsData && metricsData.metrics) {
      // Convert API response format to the format expected by savingsSlice
      const convertedMetrics = metricsData.metrics.map(metric => ({
        id: metric.id,
        transcriptionLength: metric.transcriptionLength,
        // Handle both new and old format metrics
        inputType: (metric as any).inputType || 'audio' as const,
        inputLength: (metric as any).inputLength || (metric.transcriptionLength / 5), // Estimate for old metrics
        createdAt: metric.createdAt
      }));
      
      // Always update the metrics from the API (single source of truth)
      dispatch(setTranscriptionMetrics(convertedMetrics));
    }
  }, [metricsData, dispatch]);

  // Update settings when they arrive from the API
  useEffect(() => {
    if (settingsData) {
      dispatch(updateSavingsSettings({
        hourlyRate: settingsData.hourlyRate,
        currency: settingsData.currency,
        minutesPerTranscription: 0 // Not used anymore
      }));
    }
  }, [settingsData, dispatch]);

  // Auto-refetch metrics every 30 seconds to catch new transcriptions
  useEffect(() => {
    const interval = setInterval(() => {
      refetchMetrics();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [refetchMetrics]);

  // Function to change views correctly
  const handleViewChange = (newView: WidgetView) => {
    setView(newView);
  };

  // Menu handlers
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setView('main'); // Reset to main view when opening
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Function to save configuration changes
  const saveChanges = async () => {
    const newHourlyRate = parseFloat(localHourlyRate) || 0;
    
    try {
      // Save to backend first
      await updateTimeSavingsSettings({
        hourlyRate: newHourlyRate,
        currency: settings.currency
      }).unwrap();
      
      // Update Redux state only after successful backend save
      dispatch(updateSavingsSettings({ 
        hourlyRate: newHourlyRate,
        currency: settings.currency,
        minutesPerTranscription: 0 // Not using this anymore since we use actual input data
      }));
      
      setView('main');
    } catch (error) {
      console.error('Error saving settings:', error);
      // Could show a toast notification here for user feedback
    }
  };

  // Handle currency change
  const handleCurrencyChange = async (value: string) => {
    try {
      // Save to backend first
      await updateTimeSavingsSettings({
        hourlyRate: settings.hourlyRate,
        currency: value
      }).unwrap();
      
      // Update Redux state only after successful backend save
      dispatch(updateSavingsSettings({ 
        currency: value,
        hourlyRate: settings.hourlyRate,
        minutesPerTranscription: 0 // Not using this anymore
      }));
    } catch (error) {
      console.error('Error saving currency change:', error);
      // Could show a toast notification here for user feedback
    }
  };

  // Effect to load user configuration
  useEffect(() => {
    setLocalHourlyRate(settings.hourlyRate.toString());
  }, [settings.hourlyRate]);

  // Rendering for the trigger button
  const renderTriggerButton = () => (
    <Tooltip title="Time & Cost Savings">
      <Button
        onClick={handleOpenMenu}
        color="inherit"
        sx={{
          bgcolor: '#121212',
          color: 'white',
          padding: '6px 12px',
          borderRadius: '8px',
          textTransform: 'none',
          '&:hover': { bgcolor: '#1a1a1a' },
          // Mobile-specific styling to center the text
          '& .MuiButton-startIcon': {
            display: { xs: 'none', sm: 'flex' }
          },
          justifyContent: 'center',
          alignItems: 'center'
        }}
        startIcon={<AccessTimeIcon sx={{ color: '#9575cd' }} />}
      >
        <Typography variant="body2" fontWeight="medium" sx={{ textTransform: 'none' }}>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            Saved {isLoading ? "..." : formatTimeDisplayForNavbar(totalTimeSavedSeconds)}
          </Box>
          <Box sx={{ display: { xs: 'block', sm: 'none' }, textAlign: 'center' }}>
            Savings
          </Box>
        </Typography>
        <ExpandMoreIcon sx={{ ml: 0.5, fontSize: 18 }} />
      </Button>
    </Tooltip>
  );

  // Rendering for main menu content
  const renderMainContent = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AccessTimeIcon sx={{ mr: 1, color: '#9575cd' }} fontSize="small" />
          <Typography variant="body2" color="rgba(255,255,255,0.8)">
            Total Time Saved
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight="medium">
          {isLoading ? "..." : totalTimeSaved}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TodayIcon sx={{ mr: 1, color: '#4caf50' }} fontSize="small" />
          <Typography variant="body2" color="rgba(255,255,255,0.8)">
            Today's Time Saved
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight="medium">
          {isLoading ? "..." : todayTimeSaved}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AttachMoneyIcon sx={{ mr: 1, color: '#ffd700' }} fontSize="small" />
          <Typography variant="body2" color="rgba(255,255,255,0.8)">
            Total Cost Saved
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight="medium">
          {isLoading ? "..." : `${getCurrencySymbol()}${totalCostSaved.toFixed(2)}`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AttachMoneyIcon sx={{ mr: 1, color: '#ffd700' }} fontSize="small" />
          <Typography variant="body2" color="rgba(255,255,255,0.8)">
            Today's Cost Saved
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight="medium">
          {isLoading ? "..." : `${getCurrencySymbol()}${todayCostSaved.toFixed(2)}`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => handleViewChange('settings')}
          sx={{ color: '#5D5FEF' }}
        >
          Settings
        </Button>
      </Box>
    </Box>
  );

  // Rendering for settings content
  const renderSettingsContent = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton size="small" onClick={() => handleViewChange('main')} sx={{ mr: 1 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight="medium">
          Settings
        </Typography>
      </Box>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        Hourly Rate
      </Typography>
      <TextField
        fullWidth
        value={localHourlyRate}
        onChange={(e) => setLocalHourlyRate(e.target.value)}
        variant="outlined"
        size="small"
        inputProps={{ style: { color: 'white' } }}
        sx={{ 
          mb: 2, 
          '.MuiOutlinedInput-root': {
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#5D5FEF',
            }
          } 
        }}
      />
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        Currency
      </Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <Select
          value={settings.currency}
          onChange={(e) => handleCurrencyChange(e.target.value)}
          size="small"
          sx={{ 
            color: 'white',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#5D5FEF',
            }
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                bgcolor: '#1a1f2e',
                color: 'white',
              }
            }
          }}
        >
          {currencies.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        fullWidth
        variant="contained"
        disabled={isSavingSettings}
        sx={{ 
          bgcolor: '#5D5FEF', 
          color: 'white',
          '&:hover': {
            bgcolor: '#4a4ccf'
          },
          '&:disabled': {
            bgcolor: 'rgba(93, 95, 239, 0.5)'
          }
        }}
        onClick={saveChanges}
      >
        {isSavingSettings ? 'Saving...' : 'Save Changes'}
      </Button>
    </Box>
  );

  // Hide widget on mobile for free users
  if (isMobile && !isPro) {
    return null;
  }

  return (
    <>
      {/* Trigger Button */}
      {renderTriggerButton()}
      
      {/* Menu with Widget Content */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            width: 300,
            bgcolor: '#121212',
            color: 'white',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)',
            borderRadius: '8px',
            mt: 1,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: '#121212',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          bgcolor: '#5D5FEF', 
          p: 1.5,
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Time & Cost Savings
          </Typography>
        </Box>
        
        {view === 'main' && renderMainContent()}
        {view === 'settings' && renderSettingsContent()}
      </Menu>
    </>
  );
};

export default TimeSavingsWidget; 